import type { MaybeRefOrGetter } from 'vue';
import type { Morphs, Media, MediaCollection, updateMedia_Body } from '@/api';
import { useEventListener } from '@vueuse/core';
import type { AxiosProgressEvent } from 'axios';
import type { z } from 'zod';

export const useMediaCollection = (
    media: Ref<Media[]>,
    input: MaybeRefOrGetter<HTMLInputElement | undefined | null>,
    target: MaybeRefOrGetter<{
        collection: string | MediaCollection | undefined;
        immediate?: boolean;
        attachableType: Morphs;
        attachableId: number | undefined;
        documentTypeId?: string | undefined | null;
    }>,
    behavior?: {
        showRequirements?: boolean;
    },
) => {
    const collectionName = computed(() => {
        const tg = toValue(target);
        return tg.collection == null
            ? undefined
            : typeof tg.collection === 'string'
              ? tg.collection
              : tg.collection.collection_name;
    });
    const label = computed(() => {
        const tg = toValue(target);
        return tg.collection == null
            ? undefined
            : typeof tg.collection === 'string'
              ? tg.collection
              : tg.collection.display_name;
    });
    const collectionMedia = computed(() => media.value.filter(m => m.collection_name === collectionName.value));
    const config = computed(() => {
        const tg = toValue(target);
        return {
            remainingUploads:
                typeof tg.collection === 'string' || tg.collection == null
                    ? Number.MAX_SAFE_INTEGER
                    : tg.collection.max_media_count == null
                      ? Number.MAX_SAFE_INTEGER
                      : Math.max(0, tg.collection.max_media_count - collectionMedia.value.length),
        };
    });

    const uploadReady = ref(false);
    const uploadFiles = ref<File[]>();
    const uploadNames = ref<string[]>([]);

    const onInputChange = (e: Event) => {
        if (!(e.target instanceof HTMLInputElement)) return;
        uploadReady.value = e.target.files != null && e.target.files.length > 0;
        uploadFiles.value = e.target.files != null ? Array.from(e.target.files) : undefined;
        uploadNames.value = e.target.files != null ? Array.from(e.target.files).map(f => f.name) : [];
        if (toValue(target).immediate !== false) {
            handleFileList(e.target.files);
        }
    };

    const handleFileList = (files: FileList | null) => {
        if (files == null || files.length === 0) return;
        const tg = toValue(target);
        if (typeof tg.collection !== 'string' && tg.collection != null && !tg.collection.keeps_latest) {
            if (config.value.remainingUploads === 0) {
                toast.info('You have reached the maximum number of uploads for this type of media.');
            } else {
                uploadMedia(Array.from(files).slice(0, config.value.remainingUploads));
            }
        } else {
            uploadMedia(Array.from(files));
        }
    };

    useEventListener(input, 'change', onInputChange);
    watchEffect(() => {
        const i = toValue(input);
        const tg = toValue(target);
        if (i == null || typeof tg.collection === 'string' || tg.collection == null) return;
        i.multiple = tg.collection.max_media_count != null && tg.collection.max_media_count > 1;
    });

    const { durable: uploadSucceeded, triggerDurable: onUploadSucceeded } = useDurable<boolean>({
        duration: 2500,
    });
    const uploadProgress = ref<number>();
    const uploadingItems = ref<{ name: string; url: string }[]>();
    const {
        action: uploadMedia,
        isLoading: uploading,
        error: uploadError,
    } = useAsyncAction(async (files: File[]) => {
        const tg = toValue(target);
        if (tg.attachableId == null) {
            return;
        }
        try {
            uploadProgress.value = undefined;
            uploadingItems.value = files.map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
            const { data } = await api.addMedia(
                {
                    collection_name: collectionName.value ?? 'default',
                    media: files,
                    custom_properties: files.map(f => ({ document_type_id: (tg.documentTypeId ?? undefined) as any })),
                    names: uploadNames.value,
                },
                {
                    params: {
                        attachableType: tg.attachableType,
                        attachableId: tg.attachableId,
                    },
                    onUploadProgress: (e: AxiosProgressEvent) => {
                        if (e.total != null) {
                            uploadProgress.value = Math.round((e.loaded / e.total) * 100);
                        }
                    },
                },
            );
            media.value = data;
            uploadNames.value = [];
            onUploadSucceeded(true);
        } finally {
            uploadProgress.value = undefined;
            for (const item of uploadingItems.value ?? []) {
                URL.revokeObjectURL(item.url);
            }
            uploadingItems.value = undefined;
        }
    });

    const { action: deleteMedia, isLoading: deleting } = useAsyncAction(async (toDelete: Pick<Media, 'id'>) => {
        const tg = toValue(target);
        if (tg.attachableId == null) {
            return;
        }
        const { data } = await api.deleteMedia(undefined, {
            params: {
                mediaId: toDelete.id,
            },
        });
        media.value = data;
    });

    const {
        action: updateMedia,
        isLoading: updating,
        currentArgs: updatingMedia,
    } = useAsyncAction(async (toUpdate: Media, update: z.infer<typeof updateMedia_Body>) => {
        const { data } = await api.updateMedia(update, {
            params: {
                mediaId: toUpdate.id,
            },
        });
        media.value = media.value.filter(m => m.id !== toUpdate.id).concat([data]);
    });

    const firstMedia = computed(() => {
        const tg = toValue(target);
        const inCollection = media.value.filter(m => m.collection_name === collectionName.value);

        return (
            inCollection.find(m => m.model_id === tg.attachableId && m.model_type === tg.attachableType) ??
            inCollection[0]
        );
    });

    const dialog = useDialog();

    const openInput = () => {
        if (uploading.value) return;

        if (behavior?.showRequirements) {
            const col = toValue(target)?.collection;
            let requirements = '';

            if (typeof col !== 'string' && col?.dimensions != null) {
                if (col.dimensions.max_width && col.dimensions.max_height) {
                    requirements += `Maximum resolution is ${col.dimensions.max_width}x${col.dimensions.max_height}px.<br />`;
                }
                if (col.dimensions.min_width && col.dimensions.min_height) {
                    requirements += `Minimum resolution is ${col.dimensions.min_width}x${col.dimensions.min_height}px.<br />`;
                }
            }

            if (requirements !== '') {
                dialog.confirm(requirements, {
                    title: 'Media Requirements',
                    agree: {
                        text: 'Continue',
                        onClick: () => toValue(input)?.click(),
                    },
                });
                return;
            }
        }

        toValue(input)?.click();
    };

    const startUpload = () => {
        if (uploading.value) return;
        handleFileList(toValue(input)?.files ?? null);
    };

    return {
        uploadMedia,
        deleteMedia,
        uploadNames,
        deleting,
        openInput,
        startUpload,
        updateMedia,
        uploadError,
        firstMedia,
        uploading,
        updating,
        config,
        collectionMedia,
        uploadFiles,
        uploadReady,
        uploadSucceeded,
        uploadProgress,
        uploadingItems,
        updatingMedia,
    };
};
